import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ShieldAlert, ShieldCheck, RefreshCw, OctagonX, PlayCircle, Gauge, Save, Flame } from 'lucide-react';
import { apiGet, apiPost, apiPut, formatTs, BOT_LABELS } from './ops_helpers';

/**
 * Parada de Emergencia (kill switch). Permite PARAR toda la operativa (global)
 * o un bot concreto al instante. Los bots leen GET /api/ops/killswitch/{bot_id}
 * en cada loop y no abren nueva operativa cuando halted=True.
 */
const StateBadge = ({ enabled, testId }) => (
  enabled ? (
    <Badge data-testid={testId} className="bg-rose-600/20 text-rose-300 border-rose-700/50">
      <ShieldAlert className="h-3.5 w-3.5 mr-1" /> PARADO
    </Badge>
  ) : (
    <Badge data-testid={testId} className="bg-emerald-600/20 text-emerald-300 border-emerald-700/40">
      <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Operando
    </Badge>
  )
);

export function EmergencyStopPanel({ bots = [], isAdmin = false, onStateChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(null); // { target, enabled, label }
  const [flatten, setFlatten] = useState(null);          // estado flatten por bot
  const [flattenConfirm, setFlattenConfirm] = useState(null); // { target, label }
  const [busy, setBusy] = useState(false);
  const [ap, setAp] = useState(null);           // config auto-proteccion
  const [apSaving, setApSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, apCfg, fl] = await Promise.all([
        apiGet('/api/ops/killswitch'),
        apiGet('/api/ops/autoprotect').catch(() => null),
        apiGet('/api/ops/flatten').catch(() => null),
      ]);
      setData(d);
      if (apCfg) setAp(apCfg);
      if (fl) setFlatten(fl.flatten || {});
      if (onStateChange) {
        const halted = (d.global?.enabled ? 1 : 0) +
          Object.values(d.bots || {}).filter((b) => b.enabled).length;
        onStateChange(halted, !!d.global?.enabled);
      }
    } catch (e) {
      toast.error(`Error cargando parada de emergencia: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [onStateChange]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const doToggle = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await apiPost(`/api/ops/killswitch/${confirm.target}`, {
        enabled: confirm.enabled,
        reason: confirm.enabled ? (reason || 'Parada manual desde el dashboard') : (reason || null),
      });
      toast.success(
        confirm.enabled
          ? `Parada ACTIVADA: ${confirm.label}`
          : `Operativa reanudada: ${confirm.label}`
      );
      setReason('');
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const updateAp = (patch) => setAp((s) => ({ ...(s || {}), ...patch }));

  const doFlatten = async () => {
    if (!flattenConfirm) return;
    setBusy(true);
    try {
      const r = await apiPost(`/api/ops/flatten/${flattenConfirm.target}`, {
        confirm: true,
        reason: reason || 'Flatten manual desde el dashboard',
      });
      toast.success(`Flatten solicitado: ${(r.targets || []).join(', ')}. Los bots cerrarán en segundos.`);
      setReason('');
      setFlattenConfirm(null);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(`Error en flatten: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const saveAutoProtect = async () => {
    setApSaving(true);
    try {
      const payload = {
        enabled: !!ap?.enabled,
        dd_threshold_pct: Number(ap?.dd_threshold_pct) || 0,
        rapid_drop_pct: Number(ap?.rapid_drop_pct) || 0,
        rapid_window_min: parseInt(ap?.rapid_window_min, 10) || 1,
        dd_consecutive: parseInt(ap?.dd_consecutive, 10) || 1,
        rapid_consecutive: parseInt(ap?.rapid_consecutive, 10) || 1,
        glitch_drop_pct: Number(ap?.glitch_drop_pct) || 50,
        min_equity_usd: Number(ap?.min_equity_usd) || 0,
      };
      const r = await apiPut('/api/ops/autoprotect', payload);
      setAp((s) => ({ ...(s || {}), ...r.config }));
      toast.success(ap?.enabled ? 'Auto-protección ACTIVADA' : 'Auto-protección guardada (desactivada)');
    } catch (e) {
      toast.error(`Error guardando auto-protección: ${e.message}`);
    } finally {
      setApSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card className="bg-zinc-900/60 border-zinc-800" data-testid="emergency-no-admin">
        <CardContent className="py-8 text-center text-zinc-400">
          Acceso restringido. Solo administradores pueden usar la parada de emergencia.
        </CardContent>
      </Card>
    );
  }

  const g = data?.global || {};
  const botIds = Object.keys(data?.bots || {});
  const botList = botIds.length
    ? botIds
    : (bots && bots.length ? bots.map((b) => b.bot_id) : ['hyperliquid', 'ftmo']);

  return (
    <div className="space-y-4" data-testid="emergency-stop-panel">
      {/* GLOBAL */}
      <Card
        className={`border-2 ${g.enabled ? 'border-rose-700 bg-rose-950/30' : 'border-zinc-800 bg-zinc-900/60'}`}
        data-testid="emergency-global-card"
      >
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <OctagonX className={`h-5 w-5 ${g.enabled ? 'text-rose-400' : 'text-zinc-400'}`} />
                Parada de Emergencia · GLOBAL
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Detiene la apertura de nueva operativa en <b>TODOS</b> los bots. Las posiciones
                abiertas mantienen su SL/TP nativo; solo se bloquean entradas nuevas.
              </CardDescription>
            </div>
            <StateBadge enabled={!!g.enabled} testId="emergency-global-state" />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {g.enabled && (
            <div className="rounded border border-rose-800/60 bg-rose-950/40 px-3 py-2 text-sm text-rose-200" data-testid="emergency-global-reason">
              Motivo: <b>{g.reason || '—'}</b> · por {g.updated_by || '—'} · {formatTs(g.updated_at)}
            </div>
          )}
          <Input
            data-testid="emergency-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo de la parada (ej. flash-crash XAUUSD, mantenimiento VPS)"
            className="bg-zinc-950 border-zinc-800 text-zinc-200"
          />
          <div className="flex flex-wrap gap-2">
            {!g.enabled ? (
              <Button
                data-testid="emergency-global-halt-btn"
                onClick={() => setConfirm({ target: 'all', enabled: true, label: 'TODOS los bots' })}
                className="bg-rose-600 hover:bg-rose-500 text-white font-semibold"
              >
                <OctagonX className="h-4 w-4 mr-1" /> PARAR TODOS LOS BOTS
              </Button>
            ) : (
              <Button
                data-testid="emergency-global-resume-btn"
                onClick={() => setConfirm({ target: 'all', enabled: false, label: 'TODOS los bots' })}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
              >
                <PlayCircle className="h-4 w-4 mr-1" /> Reanudar operativa global
              </Button>
            )}
            <Button
              data-testid="emergency-refresh-btn"
              variant="ghost"
              className="text-zinc-400 hover:text-zinc-100 ml-auto"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Recargar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* FLATTEN (cerrar todo) */}
      <Card className="border-2 border-orange-800/70 bg-orange-950/20" data-testid="flatten-card">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-400" />
            Cerrar TODO (Flatten)
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Cierra <b>posiciones abiertas</b> y cancela <b>órdenes pendientes</b>, además de bloquear nueva
            operativa. <b className="text-orange-300">Irreversible</b> (realiza el PnL). Los bots ejecutan en
            su siguiente ciclo (segundos).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="flatten-all-btn"
              onClick={() => setFlattenConfirm({ target: 'all', label: 'TODOS los bots' })}
              className="bg-orange-600 hover:bg-orange-500 text-white font-semibold"
            >
              <Flame className="h-4 w-4 mr-1" /> CERRAR TODO AHORA
            </Button>
            {botList.map((id) => (
              <Button
                key={id}
                data-testid={`flatten-bot-${id}`}
                variant="outline"
                className="border-orange-800/70 text-orange-300 hover:bg-orange-950/50 h-9"
                onClick={() => setFlattenConfirm({ target: id, label: BOT_LABELS[id] || id })}
              >
                <Flame className="h-3.5 w-3.5 mr-1" /> {BOT_LABELS[id] || id}
              </Button>
            ))}
          </div>
          {flatten && Object.keys(flatten).length > 0 && (
            <div className="space-y-1 text-xs">
              {Object.entries(flatten).map(([id, f]) => (
                <div key={id} data-testid={`flatten-status-${id}`} className="text-zinc-400">
                  <span className="text-zinc-300">{BOT_LABELS[id] || id}:</span>{' '}
                  {f.status === 'pending' ? (
                    <span className="text-amber-300">solicitado, esperando al bot…</span>
                  ) : f.status === 'done' ? (
                    <span className="text-emerald-300">
                      ✓ {f.result?.closed_positions ?? 0} cerradas, {f.result?.cancelled_orders ?? 0} canceladas · {formatTs(f.acked_at)}
                    </span>
                  ) : f.status === 'error' ? (
                    <span className="text-rose-300">error: {(f.result?.errors || []).join(', ') || '—'}</span>
                  ) : (<span>—</span>)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* POR BOT */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 text-base">Por bot</CardTitle>
          <CardDescription className="text-zinc-500">
            Para o reanuda un bot individual. La parada global tiene prioridad sobre estos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {botList.map((id) => {
            const b = data?.bots?.[id] || {};
            const blockedByGlobal = !!g.enabled;
            return (
              <div
                key={id}
                data-testid={`emergency-bot-row-${id}`}
                className="flex items-center justify-between gap-3 rounded border border-zinc-800 bg-zinc-950/40 px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-100">{BOT_LABELS[id] || id}</p>
                  {b.enabled && (
                    <p className="text-xs text-rose-300/80 truncate">
                      {b.reason || 'parado'} · {formatTs(b.updated_at)}
                    </p>
                  )}
                  {!b.enabled && blockedByGlobal && (
                    <p className="text-xs text-amber-300/70">bloqueado por la parada global</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StateBadge enabled={!!b.enabled || blockedByGlobal} testId={`emergency-bot-state-${id}`} />
                  {!b.enabled ? (
                    <Button
                      data-testid={`emergency-bot-halt-${id}`}
                      size="sm"
                      className="bg-rose-600/90 hover:bg-rose-500 h-8"
                      onClick={() => setConfirm({ target: id, enabled: true, label: BOT_LABELS[id] || id })}
                    >
                      <OctagonX className="h-3.5 w-3.5 mr-1" /> Parar
                    </Button>
                  ) : (
                    <Button
                      data-testid={`emergency-bot-resume-${id}`}
                      size="sm"
                      className="bg-emerald-600/90 hover:bg-emerald-500 h-8"
                      onClick={() => setConfirm({ target: id, enabled: false, label: BOT_LABELS[id] || id })}
                    >
                      <PlayCircle className="h-3.5 w-3.5 mr-1" /> Reanudar
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* AUTO-PROTECCION */}
      <Card
        className={`bg-zinc-900/60 ${ap?.enabled ? 'border-amber-700/60' : 'border-zinc-800'}`}
        data-testid="autoprotect-card"
      >
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-zinc-100 flex items-center gap-2">
                <Gauge className={`h-5 w-5 ${ap?.enabled ? 'text-amber-400' : 'text-zinc-400'}`} />
                Auto-protección anti flash-crash
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Dispara automáticamente la parada de un bot si su drawdown o una caída
                rápida de equity superan los umbrales. Requiere reanudación manual.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="ap-enabled" className="text-sm text-zinc-300">
                {ap?.enabled ? 'Activa' : 'Inactiva'}
              </Label>
              <Switch
                id="ap-enabled"
                data-testid="autoprotect-enabled-switch"
                checked={!!ap?.enabled}
                onCheckedChange={(v) => updateAp({ enabled: v })}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Drawdown desde peak (%)</Label>
              <Input
                data-testid="autoprotect-dd-input"
                type="number" step="0.5" min="0" max="100"
                value={ap?.dd_threshold_pct ?? ''}
                onChange={(e) => updateAp({ dd_threshold_pct: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Caída rápida (%)</Label>
              <Input
                data-testid="autoprotect-rapid-input"
                type="number" step="0.5" min="0" max="100"
                value={ap?.rapid_drop_pct ?? ''}
                onChange={(e) => updateAp({ rapid_drop_pct: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Ventana (min)</Label>
              <Input
                data-testid="autoprotect-window-input"
                type="number" step="1" min="1" max="240"
                value={ap?.rapid_window_min ?? ''}
                onChange={(e) => updateAp({ rapid_window_min: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
          </div>
          {/* Anti falsa-alarma (debounce + guard de glitch) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">DD consec.</Label>
              <Input
                data-testid="autoprotect-dd-consec-input"
                type="number" step="1" min="1" max="10"
                value={ap?.dd_consecutive ?? ''}
                onChange={(e) => updateAp({ dd_consecutive: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Caída consec.</Label>
              <Input
                data-testid="autoprotect-rapid-consec-input"
                type="number" step="1" min="1" max="10"
                value={ap?.rapid_consecutive ?? ''}
                onChange={(e) => updateAp({ rapid_consecutive: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Techo glitch (%)</Label>
              <Input
                data-testid="autoprotect-glitch-input"
                type="number" step="1" min="10" max="100"
                value={ap?.glitch_drop_pct ?? ''}
                onChange={(e) => updateAp({ glitch_drop_pct: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-zinc-400 text-xs">Equity mín. ($)</Label>
              <Input
                data-testid="autoprotect-min-equity-input"
                type="number" step="1" min="0"
                value={ap?.min_equity_usd ?? ''}
                onChange={(e) => updateAp({ min_equity_usd: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-200"
              />
            </div>
          </div>
          <p className="text-xs text-zinc-500">
            <b>Anti falsa-alarma:</b> exige N lecturas consecutivas para confirmar; una caída/DD por
            encima del <b>techo de glitch</b> se descarta como fallo de datos (no para el bot); un equity
            por debajo del <b>mínimo</b> se ignora (lectura inválida).
          </p>
          <div className="flex items-center gap-3">
            <Button
              data-testid="autoprotect-save-btn"
              onClick={saveAutoProtect}
              disabled={apSaving}
              className="bg-amber-600 hover:bg-amber-500 text-zinc-950 font-semibold"
            >
              <Save className="h-4 w-4 mr-1" /> {apSaving ? 'Guardando...' : 'Guardar auto-protección'}
            </Button>
            {ap?.updated_at && (
              <span className="text-xs text-zinc-500">
                Últ. cambio: {ap.updated_by || '—'} · {formatTs(ap.updated_at)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* CONFIRMACION */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="emergency-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-100">
              {confirm?.enabled ? '¿Parar la operativa?' : '¿Reanudar la operativa?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirm?.enabled
                ? `Vas a PARAR: ${confirm?.label}. Dejarán de abrirse trades nuevos hasta que reanudes.`
                : `Vas a REANUDAR: ${confirm?.label}. Los bots volverán a operar con normalidad.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="emergency-confirm-cancel" className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="emergency-confirm-accept"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); doToggle(); }}
              className={confirm?.enabled ? 'bg-rose-600 hover:bg-rose-500' : 'bg-emerald-600 hover:bg-emerald-500'}
            >
              {busy ? 'Aplicando...' : (confirm?.enabled ? 'Sí, PARAR' : 'Sí, reanudar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CONFIRMACION FLATTEN */}
      <AlertDialog open={!!flattenConfirm} onOpenChange={(o) => { if (!o) setFlattenConfirm(null); }}>
        <AlertDialogContent className="bg-zinc-900 border-orange-800" data-testid="flatten-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-orange-200 flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-400" /> ¿Cerrar TODO?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Vas a CERRAR posiciones y CANCELAR pendientes de <b>{flattenConfirm?.label}</b>, y bloquear
              nueva operativa. Es <b className="text-orange-300">irreversible</b> y realiza el PnL actual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="flatten-confirm-cancel" className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="flatten-confirm-accept"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); doFlatten(); }}
              className="bg-orange-600 hover:bg-orange-500"
            >
              {busy ? 'Cerrando...' : 'Sí, CERRAR TODO'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default EmergencyStopPanel;
